import analytics from '../analytics';
/**
 * @class
 * @classdesc Generic form send module. When user hits enter or clicks submit
 *            this module will gather information and send 'em to back-end
 * @author Eero Ahola <eero.ahola@crasman.fi>
 *
 * @example
 * // Create HTML form with class name send-form-ajax & attention! Needs inject-cm-token -class too!
 * // Add endpoint to action
 * // Default method is POST, currently supports POST only
 *
 * // If error in first and lastname, clear firstname at the same time when addin data to lastanme
 * // For example used in cases when either phone number or email address is mandatory
 *
 * // HTML:
 * <form class="send-form-ajax inject-cm-token" action="/stage-ajax/nön" method="POST">
 *   <input type="text" name="firstname" data-clear-also="lastname">
 *   <input type="text" name="lastname" data-clear-also="firstname">
 *   <input type="submit" value="Send">
 * </form>
 */
class FormSend
{
  /**
   * Constructor of FormSend. Fired from static init function
   *
   * @param {object} formElement - Form element got from querySelector
   */
  constructor(formElement) {
    this._successTemplate = `<div class="form-send-success text-center">
      <h2 class="alpha black caps">Kiitos yhteydenottopyynnöstä</h2>
      <p class="epsilon">Olemme sinuun yhteydessä mahdollisimman pian!</p>
    </div>`;

    this._addListeners(formElement);
  }

  /**
   * Add necessary event listeners for form
   *
   * @param {object} formElement - From element got from querySelector
   *
   * @returns {undefined}
   * @private
   */
  _addListeners(formElement) {
    formElement.addEventListener('submit', this._submitEvent.bind(this));
    // Key up event listener for every text input for clearing possible error
    const formInputs = formElement.querySelectorAll('input, textarea');
    for(const formInput of formInputs) {
      formInput.addEventListener('keyup', this._clearInputError.bind(this));
    }
  }

  /**
   * Clear input error from single input
   *
   * @param {Event} event - KeyUp event
   *
   * @returns {undefined}
   * @private
   */
  _clearInputError(event) {
    if(event.target.classList.contains('input-error')) {
      event.target.classList.remove('input-error');
    }

    // In some cases, same input must clear errors from another input
    // Define it with data-clear-also -data attribute
    if(event.target.dataset.clearAlso) {
      const clearAlsoInput = event.target.closest('form')
        .querySelector(`[name="${event.target.dataset.clearAlso}"]`);

      if(clearAlsoInput && clearAlsoInput.classList.contains('input-error')) {
        clearAlsoInput.classList.remove('input-error')
      }
    }
  }

  /**
   * Clean input-errors -classes from form
   *
   * @param {object} formElement - Form to clean up
   *
   * @returns {undefined}
   * @private
   */
  _clearAllFormErrors(formElement) {
    const inputsInForm = formElement.querySelectorAll('.input-error');
    for(const input of inputsInForm) {
      input.classList.remove('input-error');
    }
  }

  /**
   * Submit event for form
   *
   * @param {Event} event - Form submit event
   *
   * @returns {undefined}
   * @private
   */
  _submitEvent(event) {
    event.preventDefault();
    // Disable submit button
    event.target.querySelector('[type="submit"]').disabled = true;

    // Clear errors
    this._clearAllFormErrors(event.target);
    this._sendFormData(
      new URLSearchParams(new FormData(event.target)).toString(),
      event.target.action,
      event.target.method
    ).then(response => {
      if(response.errors.length > 0) {
        for(const errorField of response.errors) {
          if(errorField !== 'reCaptcha') {
            event.target.querySelector(`[name="${errorField}"]`)
              .classList.add('input-error');
          }
        }
        event.target.querySelector('[type="submit"]').disabled = false;

        // Reset reCaptcha button if exists
        if(typeof grecaptcha === 'object') {
          grecaptcha.reset(); // eslint-disable-line no-undef
        }
      } else {
        event.target.closest('form').innerHTML = this._successTemplate;
      }
    });
  }

  /**
   * Send data to server
   *
   * @param {FormData} formData - Form data
   * @param {string}   endPoint - Endpoint string
   * @param {string}   method   - POST | GET | PUT
   *
   * @returns {object} - JSON object from server
   *
   * @async
   * @private
   */
  async _sendFormData(formData, endPoint, method) {
    const rawResponse = await fetch(endPoint, {
      method,
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: formData
    })
      .then(response => response.json())
      .then(response => {
      // Update cmtokens
        this._updateCmTokens(response.cmToken);

        if(response.dataLayerConversion) {
          analytics.pushToDataLayer({
            event: response.dataLayerConversion.event,
            conversion: {
              name: response.dataLayerConversion.name,
              id: response.dataLayerConversion.id
            }
          });
        }

        return response;
      })
      .catch(error => {
        console.log(error);
      });

    return rawResponse;
  }

  /**
   * Update all form cm token when ajax-request has done
   *
   * @param {string} newToken - New stage-token
   *
   * @returns {undefined}
   * @private
   */
  _updateCmTokens(newToken) {
    const cmTokenFields = document.querySelectorAll('[name="cmtoken"]');
    for(const tokenField of cmTokenFields) {
      tokenField.value = newToken;
    }
  }

  /**
   * Initialize forms
   *
   * @returns {undefined}
   * @static
   */
  static init() {
    const forms = document.querySelectorAll('form.send-form-ajax');
    if(forms.length > 0) {
      for(const form of forms) {
        new FormSend(form);
      }
    }
  }
}

export default FormSend;
